import { useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { Music } from "lucide-react";
import type { components } from "@cntrl/api-client";
import { Card, HeadRail, Icon, StatBlock } from "@cntrl/ui";
import { ReleaseArtwork } from "./ReleaseArtwork.js";
import styles from "./RecentReleaseCard.module.scss";

export type RecentReleaseBlock = components["schemas"]["RecentReleaseBlock"];

/**
 * The "Most recent release" showcase — ONE card, rendered identically by the Dashboard's analytics
 * column and by the Streaming (Music) tab.
 *
 * **Why it is shared.** `DashboardAnalytics.recentRelease` and `MusicView.recentRelease` are the SAME
 * `RecentReleaseBlock`, composed by the same `AnalyticsComposition.BuildFeaturedReleaseAsync` call — the
 * backend unified them so the two surfaces could not drift. The UI had drifted anyway: two headers, two
 * meta-line formatters, two stat-row implementations and two copies of the sub-line (one saying "artist's",
 * one "band's"). This is the one card; the callers supply only what genuinely differs.
 *
 * **Anatomy** (the reference's, and the Dashboard's measured proportions):
 * a mono KICKER, the release's own name as the display headline beneath it, the purpose line, then cover
 * art beside a meta line and a two-column stat list read DOWN with rules between rows. The "next release"
 * pill rides top-right in the card header, level with the kicker.
 *
 * **The kicker is the heading, dressed** — STYLE-GUIDE ruling 7 ("the treatment decides, not the tag"): it
 * stays a real heading so the outline and `aria-labelledby` work, while reading as a mono eyebrow. Its LEVEL
 * is the caller's, because the two surfaces sit at different depths: the Music tab is a page-level region
 * under an `h1` (`h2`), the dashboard a card inside an already-titled column (`h3`). Getting this wrong is
 * what axe's `heading-order` flags, and the Music suite pins it explicitly.
 *
 * **No release is NOT an empty state here.** This is the one place the design deliberately shows dashes:
 * the card keeps its eight labelled rows and renders each as a muted `—`, with "No releases yet" standing in
 * for the release's name. The hero's "never pad with dashes" rule is hero-scoped.
 */
export function RecentReleaseCard({
  rr,
  headingLevel = "h3",
  headingId,
  className,
}: {
  /** The composed block, or `null` for a band with no release yet. */
  rr: RecentReleaseBlock | null;
  /** The kicker's heading level — `h2` for a page-level region, `h3` inside a titled column. */
  headingLevel?: "h2" | "h3";
  /** Wired to the section's `aria-labelledby`, so the card is a NAMED region. */
  headingId: string;
  className?: string;
}) {
  const Kicker = headingLevel;
  const stats = rr?.stats;

  // The reference splits this line: the TYPE is its own accent-coloured, uppercased, tracked span and the
  // rest stays sentence-case mono (`assets.css:386-387`). Uppercasing the whole joined string shouts the
  // date and the relative label too.
  const releaseType = (rr?.releaseType ?? "").trim() || "—";
  // An unreleased feature dashes the date and says "Upcoming" rather than counting days toward a drop that
  // has not happened. With no release at all, both halves say so in words — never a fabricated date.
  const dateText = rr
    ? rr.isUpcoming
      ? "—"
      : (rr.releaseDate ? formatFullDate(rr.releaseDate) : "no date set")
    : "no date set";
  const relText = rr
    ? rr.isUpcoming
      ? "Upcoming"
      : (formatReleaseRelative(rr.releaseDate) ?? "not released")
    : "not released";

  return (
    <section className={clsx(styles.wrap, className)} aria-labelledby={headingId}>
      <Card className={styles.card}>
        <header className={styles.head}>
          <div className={styles.headMain}>
            <Kicker id={headingId} className={styles.kicker}>
              Most recent release
            </Kicker>
            {/* The card's real headline. Deliberately NOT a heading: the kicker above it already names the
                region, and a second heading here would add an outline level on both surfaces for a string
                that is a value, not a section. */}
            <p className={styles.title}>{rr?.title ?? "No releases yet"}</p>
            {/* The invented "Streaming performance and rollout signals…" sub-line was removed (PO,
                2026-09-13): the kicker + release name already say what the card is. */}
          </div>
          {rr?.upcoming && (
            <HeadRail>
              <UpcomingPill upcoming={rr.upcoming} />
            </HeadRail>
          )}
        </header>

        <div className={styles.body}>
          {rr ? (
            <ReleaseArtwork
              seed={rr.releaseId}
              mediaId={rr.coverMediaId}
              imageUrl={rr.coverImageUrl}
              size="tile"
              lazy={false}
            />
          ) : (
            <div className={styles.coverWell} aria-hidden="true">
              <Icon icon={Music} size={24} />
            </div>
          )}

          <div className={styles.detail}>
            <p className={styles.meta}>
              <span className={styles.type}>{releaseType}</span>
              {` · ${dateText} · ${relText}`}
            </p>

            {/* The fixed eight rows, in the reference's order and wording. Two columns read DOWN, not a KPI
                row read across — each label pairs with its value on one baseline, ruled between rows.
                `StatBlock` owns the null → `—` contract, so no caller can render a fallback number. */}
            <div className={styles.statRows}>
              <ReleaseStat label="Day in cycle" value={stats?.dayInCycle} prefix="D" exact />
              <ReleaseStat label="Landing visits · MTD" value={stats?.landingVisitsMtd} />
              <ReleaseStat label="Smart-Link · MTD" value={stats?.smartLinkClicksMtd} />
              <ReleaseStat label="Spotify streams" value={stats?.spotifyStreams} />
              <ReleaseStat label="Spotify popularity" value={stats?.spotifyPopularity} suffix="/100" exact />
              <ReleaseStat label="Playlists" value={stats?.playlists} exact />
              <ReleaseStat label="Playlist reach" value={stats?.playlistReach} />
              <ReleaseStat label="Editorial picks" value={stats?.editorialPicks} exact />
            </div>
          </div>
        </div>
      </Card>
    </section>
  );
}

/** The showcase stat arrangement, named once so the eight sibling rows carry only what differs. */
const ReleaseStat = (props: Omit<Parameters<typeof StatBlock>[0], "layout">) => (
  <StatBlock layout="showcase" {...props} />
);

/**
 * The "next release" pill — a BUTTON routing to Releases (defect fix D-F4; it was static text). Router
 * navigation, never an `<a href>` with `preventDefault`.
 */
function UpcomingPill({ upcoming }: { upcoming: NonNullable<RecentReleaseBlock["upcoming"]> }) {
  const navigate = useNavigate();
  return (
    <button type="button" className={styles.upcoming} onClick={() => void navigate({ to: "/releases" })}>
      <span className={styles.upcomingLabel}>Upcoming</span>
      <span className={styles.upcomingRow}>
        {/* The real cover, like the card's own tile above. Passing `seed` alone left this chip on the
            deterministic identity gradient forever — which is exactly what a release with NO artwork looks
            like, so the omission read as "this release has no cover" rather than as a field nobody wired. */}
        <ReleaseArtwork
          seed={upcoming.releaseId}
          mediaId={upcoming.coverMediaId}
          imageUrl={upcoming.coverImageUrl}
          size="chip"
          className={styles.upcomingArt}
        />
        <span className={styles.upcomingText}>
          <span className={styles.upcomingTitle}>{upcoming.title}</span>
          <span className={styles.upcomingDate}>{formatMonthDay(upcoming.releaseDate) || "TBA"}</span>
        </span>
      </span>
    </button>
  );
}

/** Zoneless domain date: parse `YYYY-MM-DD` at local noon, never UTC. */
const parseDomainDate = (iso: string) => new Date(`${iso}T12:00:00`);

const formatMonthDay = (iso: string | null | undefined) =>
  iso ? parseDomainDate(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";

const formatFullDate = (iso: string | null | undefined) =>
  iso
    ? parseDomainDate(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : "";

function formatReleaseRelative(iso: string | null | undefined, now: number = Date.now()): string | null {
  if (!iso) return null;
  const days = Math.round((parseDomainDate(iso).getTime() - now) / 86_400_000);
  if (days === 0) return "released today";
  if (days > 0) return days === 1 ? "in 1 day" : `in ${days} days`;
  const ago = -days;
  if (ago < 30) return ago === 1 ? "released 1 day ago" : `released ${ago} days ago`;
  const months = Math.round(ago / 30);
  if (months < 12) return months === 1 ? "released 1 month ago" : `released ${months} months ago`;
  const years = Math.round(months / 12);
  return years === 1 ? "released 1 year ago" : `released ${years} years ago`;
}
